"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Grid = void 0;
const grid_js_1 = require("@ngageoint/grid-js");
const MGRSProperties_1 = require("../property/MGRSProperties");
const GridType_1 = require("./GridType");
const GridTypeUtils_1 = require("./GridTypeUtils");
/**
 * Grid
 */
class Grid extends grid_js_1.BaseGrid {
    /**
     * Constructor
     *
     * @param type
     *            grid type
     */
    constructor(type) {
        super();
        /**
         * Grid line styles
         */
        this.styles = new Map();
        this.type = type;
    }
    /**
     * Get the grid type
     *
     * @return grid type
     */
    getType() {
        return this.type;
    }
    /**
     * Is the provided grid type
     *
     * @param type
     *            grid type
     * @return true if the type
     */
    isType(type) {
        return this.type === type;
    }
    /**
     * Get the precision in meters
     *
     * @return precision meters
     */
    getPrecision() {
        return this.type;
    }
    /**
     * Get the grid type precision line style for the grid type
     *
     * @param gridType
     *            grid type
     * @return grid type line style
     */
    getStyle(gridType) {
        let style;
        if (gridType !== null && gridType !== undefined) {
            if (gridType === this.type) {
                style = super.getStyle();
            }
            else {
                style = this.styles.get(gridType);
            }
        }
        else {
            style = super.getStyle();
        }
        return style;
    }
    /**
     * Get the grid type line style for the grid type or create it
     *
     * @param gridType
     *            grid type
     * @return grid type line style
     */
    getOrCreateStyle(gridType) {
        let style = this.getStyle(gridType);
        if (!style) {
            style = new grid_js_1.GridStyle(undefined, 0);
            this.setStyle(style, gridType);
        }
        return style;
    }
    /**
     * Set the grid type precision line style
     *
     * @param gridType
     *            grid type
     * @param style
     *            grid line style
     */
    setStyle(style, gridType) {
        if (gridType === null || gridType === undefined) {
            gridType = this.type;
        }
        if (gridType < this.getPrecision()) {
            throw new Error('Grid can not define a style for a higher precision grid type. Type: ' +
                this.type +
                ', Style Type: ' +
                gridType);
        }
        if (gridType === this.type) {
            super.setStyle(style);
        }
        else {
            this.styles.set(gridType, style ? style : new grid_js_1.GridStyle(undefined, 0));
        }
    }
    /**
     * Clear the propagated grid type precision styles
     */
    clearPrecisionStyles() {
        this.styles.clear();
    }
    /**
     * Get the grid type precision line color
     *
     * @param gridType
     *            grid type
     * @return grid type line color
     */
    getColor(gridType) {
        let color;
        if (gridType !== null && gridType !== undefined) {
            const style = this.getStyle(gridType);
            if (style) {
                color = style.getColor();
            }
        }
        if (!color) {
            color = super.getColor();
        }
        return color;
    }
    /**
     * Set the grid type precision line color
     *
     * @param gridType
     *            grid type
     * @param color
     *            grid line color
     */
    setColor(color, gridType) {
        if (gridType === null || gridType === undefined) {
            gridType = this.type;
        }
        this.getOrCreateStyle(gridType).setColor(color);
    }
    /**
     * Get the grid type precision line width
     *
     * @param gridType
     *            grid type
     * @return grid type line width
     */
    getWidth(gridType) {
        let width = 0;
        const style = this.getStyle(gridType);
        if (style) {
            width = style.getWidth();
        }
        if (width === 0) {
            width = super.getWidth();
        }
        return width;
    }
    /**
     * Set the grid type precision line width
     *
     * @param gridType
     *            grid type
     * @param width
     *            grid line width
     */
    setWidth(width, gridType) {
        if (gridType === null || gridType === undefined) {
            gridType = this.type;
        }
        this.getOrCreateStyle(gridType).setWidth(width);
    }
    /**
     * Get the grid labeler
     *
     * @return grid labeler
     */
    getLabeler() {
        return super.getLabeler();
    }
    /**
     * Set the grid labeler
     *
     * @param labeler
     *            grid labeler
     */
    setLabeler(labeler) {
        super.setLabeler(labeler);
    }
    /**
     * Get the lines for the tile and zone
     *
     * @param tile
     *            tile
     * @param zone
     *            grid zone
     * @return lines
     */
    getLinesFromGridTile(tile, zone) {
        return this.getLines(tile.getZoom(), zone, tile.getBounds());
    }
    /**
     * Get the lines for the zoom, tile bounds, and zone
     *
     * @param zoom
     *            zoom level
     * @param tileBounds
     *            tile bounds
     * @param zone
     *            grid zone
     * @return lines
     */
    getLines(zoom, zone, tileBounds) {
        let lines;
        if (tileBounds && this.isLinesWithin(zoom)) {
            lines = this.getLinesFromBounds(tileBounds, zone);
        }
        return lines;
    }
    /**
     * Get the lines for the tile bounds and zone
     *
     * @param tileBounds
     *            tile bounds
     * @param zone
     *            grid zone
     * @return lines
     */
    getLinesFromBounds(tileBounds, zone) {
        return zone.getLines(tileBounds, this.type);
    }
    /**
     * Get the labels for the tile and zone
     *
     * @param tile
     *            tile
     * @param zone
     *            grid zone
     * @return labels
     */
    getLabelsFromGridTile(tile, zone) {
        return this.getLabels(tile.getZoom(), zone, tile.getBounds());
    }
    /**
     * Get the labels for the zoom, tile bounds, and zone
     *
     * @param zoom
     *            zoom level
     * @param tileBounds
     *            tile bounds
     * @param zone
     *            grid zone
     * @return labels
     */
    getLabels(zoom, zone, tileBounds) {
        let labels;
        if (this.isLabelerWithin(zoom)) {
            labels = this.getLabeler().getLabels(this.type, zone, tileBounds);
        }
        return labels;
    }
    /**
     * {@inheritDoc}
     */
    compareTo(other) {
        return this.getPrecisionCompare() - other.getPrecisionCompare();
    }
    /**
     * Get the precision in meters
     *
     * @return precision meters
     */
    getPrecisionCompare() {
        let precision = this.getPrecision();
        if (precision <= GridType_1.GridType.GZD) {
            precision = Number.MAX_SAFE_INTEGER;
        }
        return precision;
    }
    hashCode() {
        const prime = 31;
        let result = 1;
        result = prime * result + (!this.type ? 0 : GridTypeUtils_1.GridTypeUtils.hashCode(this.type));
        return result;
    }
    /**
     * {@inheritDoc}
     */
    equals(obj) {
        if (this === obj)
            return true;
        if (!obj)
            return false;
        if (typeof this !== typeof obj)
            return false;
        const other = obj;
        if (this.type !== other.type)
            return false;
        return true;
    }
    less(other) {
        return this.hashCode() < other.hashCode();
    }
}
exports.Grid = Grid;
/**
 * Default line width
 */
Grid.DEFAULT_WIDTH = MGRSProperties_1.MGRSProperties.getInstance().getDoubleProperty(true, grid_js_1.PropertyConstants.GRID.toString(), grid_js_1.PropertyConstants.WIDTH.toString());
//# sourceMappingURL=Grid.js.map